/**
 * "Dodaj z maila" (ANEKS 6b/6c/6d): faktura, dla ktorej portal nie dal ZIP-a, ale z maila
 * przyszedl XML OSOZ-EDI (i zwykle PDF z kodami EAN). "Dodaj z KSeF" (plik.format 'fa3'): ten sam modal
 * i ta sama trasa - backend rozpoznaje format (parse_fa3_invoice, GTIN z XML = EAN, bez kroku PDF).
 * Zapis przez POST /kontrola-faktur/<id>/dodaj-osoz
 * - bez kreatora i bez cennika, wiec produkty MUSZA juz byc w systemie (EAN: nauka kodow SFD,
 * PDF, dokladna nazwa). Parser nigdy nie zaklada produktow: przy BRAK_POKRYCIA nie ma "powodu
 * pominiecia" - tabelka brakujacych (kod SFD, nazwa, ilosc, EAN), admin dodaje produkty i klika
 * "Spróbuj ponownie".
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { kontrolaFakturService } from '../../services/kontrolaFakturService';
import { formatujKwote, TOOLTIP_OSOZ_BEZ_PDF, TOOLTIP_FA3 } from './format';

const btnPrimary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff' };
const btnSecondary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)' };
const th = { padding: '4px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const td = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px', verticalAlign: 'top' };

/** Komunikat bledu zapisu po kodzie z kontraktu. */
const komunikatBledu = (err) => {
  // FAKTURA_ISTNIEJE: wspolna bramka duplikatow - komunikat z serwera mowi, w ktorym sklepie i kiedy
  if (err?.errorCode === 'FAKTURA_ISTNIEJE') return err?.data?.komunikat || err?.message || 'Ten dokument jest już w systemie.';
  if (err?.errorCode === 'JUZ_W_SYSTEMIE') return 'Ten dokument jest już w systemie — odśwież listę.';
  if (err?.status === 403 || err?.isPermissionError) return 'Dodawanie z XML z maila jest tylko dla administratora.';
  if (err?.errorCode === 'PLIK_NIEPOBRANY') return err?.data?.komunikat || 'XML nie jest już dostępny na serwerze.';
  return err?.data?.komunikat || err?.message || 'Nie udało się dodać faktury z XML.';
};

const DodajOsozModal = ({ wiersz, sklepy, onZamknij, onDodano }) => {
  const sklepZSfd = wiersz?.sklep_wg_sfd?.id ?? null;
  const listaSklepow = Array.isArray(sklepy) ? sklepy.filter(s => s && s.id !== null && s.id !== undefined) : [];
  // sklep z wiersza moze nie byc na liscie (np. per_sklep bez tego sklepu) - dolozyc, zeby domyslny wybor byl mozliwy
  if (sklepZSfd !== null && !listaSklepow.some(s => Number(s.id) === Number(sklepZSfd))) {
    listaSklepow.push({ id: sklepZSfd, nazwa: wiersz.sklep_wg_sfd?.nazwa || `#${sklepZSfd}` });
  }
  const [locationId, setLocationId] = useState(sklepZSfd !== null ? String(sklepZSfd) : '');
  const [wysylanie, setWysylanie] = useState(false);
  const [blad, setBlad] = useState(null);
  const [pokrycie, setPokrycie] = useState(null);   // {brakujace: [{kod_sfd, nazwa, ilosc, ean|null}], dopasowane, z_pdf, komunikat}
  const [wynik, setWynik] = useState(null);         // {faktura_id, numer, pozycji, location_id, z_pdf, nauczono}

  const plik = wiersz?.plik || {};
  const fa3 = plik.format === 'fa3';

  const zapisz = async () => {
    if (!locationId) { setBlad('Wybierz sklep, do którego trafi faktura.'); return; }
    setWysylanie(true);
    setBlad(null);
    try {
      const dane = await kontrolaFakturService.dodajOsoz(wiersz.id, { location_id: Number(locationId) });
      setPokrycie(null);
      setWynik(dane || {});
      toast.success(`Dodano fakturę ${dane?.numer || wiersz.numer} ${fa3 ? 'z KSeF' : 'z maila'} (${dane?.pozycji ?? '?'} pozycji)`);
      if (onDodano) onDodano(dane, wiersz);
    } catch (err) {
      if (err?.errorCode === 'BRAK_POKRYCIA') {
        const d = err.data || {};
        setPokrycie({
          brakujace: Array.isArray(d.brakujace) ? d.brakujace : [],
          dopasowane: d.dopasowane,
          z_pdf: d.z_pdf,
          komunikat: d.komunikat || null,
        });
      } else if (!err?.isAuthError) {
        setBlad(komunikatBledu(err));
      }
    } finally {
      setWysylanie(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="modal-osoz-tytul" data-testid="modal-dodaj-osoz"
        style={{ backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '100%', maxWidth: '560px', padding: '1.25rem', fontSize: '12px', color: 'var(--text)', maxHeight: '90vh', overflow: 'auto' }}>
        <h5 id="modal-osoz-tytul" style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 700 }}>{fa3 ? 'Dodaj z KSeF' : 'Dodaj z maila'}</h5>
        <p style={{ margin: '0 0 10px', color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--text)' }}>{wiersz?.numer}</strong>
          {wiersz?.brutto_portal !== null && wiersz?.brutto_portal !== undefined && <> · {formatujKwote(wiersz.brutto_portal)}</>}
          {plik.nazwa && <> · plik: {plik.nazwa}</>}
          {' · '}
          {fa3 ? (
            <span data-testid="osoz-pliki" style={{ color: 'var(--info)' }} title={TOOLTIP_FA3}>XML FA(3) z KSeF</span>
          ) : (
            <span data-testid="osoz-pliki" style={{ color: plik.pdf ? 'var(--info)' : 'var(--warning)' }} title={plik.pdf ? 'XML OSOZ + PDF z kodami EAN' : TOOLTIP_OSOZ_BEZ_PDF}>
              {plik.pdf ? 'XML+PDF' : 'tylko XML'}
            </span>
          )}
          {!fa3 && plik.mail_temat && <> · temat: „{plik.mail_temat}”</>}
          {fa3 && wiersz?.ksef?.ksef_numer && <> · KSeF: <span style={{ fontFamily: 'monospace' }}>{wiersz.ksef.ksef_numer}</span></>}
        </p>

        {wynik ? (
          <div data-testid="osoz-wynik" style={{ padding: '8px 10px', borderRadius: '4px', backgroundColor: 'var(--success-light)', color: 'var(--success)', marginBottom: '12px' }}>
            Dodano fakturę <strong>{wynik.numer || wiersz?.numer}</strong>
            {wynik.pozycji !== undefined && <> ({wynik.pozycji} pozycji)</>}.
            {!fa3 && wynik.z_pdf !== undefined && <> Kody EAN {wynik.z_pdf ? 'z PDF' : 'bez PDF (nauka / nazwa)'}.</>}
            {Number(wynik.nauczono) > 0 && <> Nauczono {wynik.nauczono} {wynik.nauczono === 1 ? 'nowy kod SFD' : 'nowych kodów SFD'}.</>}
            {wynik.faktura_id && (
              <> <Link to={`/purchase-invoices/${wynik.faktura_id}`} style={{ color: 'var(--success)', fontWeight: 600 }}>Otwórz fakturę #{wynik.faktura_id}</Link></>
            )}
          </div>
        ) : (
          <>
            <div data-testid="osoz-info" style={{ padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--info-light)', color: 'var(--info)', marginBottom: '10px', fontSize: '11px' }}>
              {fa3
                ? 'XML FA(3) z KSeF bez cennika — produkty muszą już być w systemie (EAN = GTIN z XML, z nauki kodów SFD albo po dokładnej nazwie). Ceny zakupu z faktury.'
                : `XML z maila SFD bez cennika — produkty muszą już być w systemie (EAN z PDF, z nauki kodów SFD albo po dokładnej nazwie)${!plik.pdf ? '; ' + TOOLTIP_OSOZ_BEZ_PDF : ''}. Ceny zakupu z faktury.`}
            </div>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '11px', color: 'var(--text-secondary)' }}>
              Sklep{' '}
              <select aria-label="Sklep docelowy" data-testid="osoz-sklep" value={locationId} disabled={wysylanie}
                onChange={(e) => setLocationId(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', minWidth: '200px' }}>
                <option value="">— wybierz sklep —</option>
                {listaSklepow.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nazwa}{Number(s.id) === Number(sklepZSfd) ? ' (wg SFD)' : ''}</option>
                ))}
              </select>
            </label>

            {pokrycie && (
              <div data-testid="osoz-brak-pokrycia" style={{ marginBottom: '10px', padding: '8px 10px', borderRadius: '4px', backgroundColor: 'var(--warning-light)', border: '1px solid var(--warning)' }}>
                <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: '6px' }}>
                  Brak {pokrycie.brakujace.length} {pokrycie.brakujace.length === 1 ? 'produktu' : 'produktów'} w systemie
                  {pokrycie.dopasowane !== undefined && pokrycie.dopasowane !== null && (
                    <span data-testid="osoz-dopasowane" style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> · dopasowano: {pokrycie.dopasowane}</span>
                  )}
                  {!fa3 && pokrycie.z_pdf !== undefined && (
                    <span data-testid="osoz-z-pdf" style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> · {pokrycie.z_pdf ? 'EAN z PDF' : 'bez PDF'}</span>
                  )}
                </div>
                <div style={{ overflow: 'auto', marginBottom: '6px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr><th style={th}>Kod SFD</th><th style={th}>Nazwa</th><th style={{ ...th, textAlign: 'right' }}>Ilość</th><th style={th}>EAN</th></tr>
                    </thead>
                    <tbody>
                      {pokrycie.brakujace.map((b, i) => (
                        <tr key={`${b.kod_sfd || b.kod || i}`} data-testid="osoz-brakujacy">
                          <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{b.kod_sfd || b.kod || '—'}</td>
                          <td style={td}>{b.nazwa || '—'}</td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{b.ilosc ?? '—'}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap', color: b.ean ? 'var(--text)' : 'var(--text-muted)' }} title={b.ean ? 'EAN znany, ale produktu nie ma w systemie' : (fa3 ? 'EAN nieznany (brak GTIN w XML / nauce)' : 'EAN nieznany (brak w PDF / nauce)')}>{b.ean || 'brak'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ color: 'var(--text)', fontSize: '11px' }}>
                  Dodaj te produkty do systemu (np. przez cennik) i spróbuj ponownie.
                  {pokrycie.komunikat && <span style={{ color: 'var(--text-muted)' }}> ({pokrycie.komunikat})</span>}
                </div>
              </div>
            )}

            {blad && (
              <div data-testid="osoz-blad" style={{ marginBottom: '10px', padding: '6px 10px', borderRadius: '4px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)' }}>{blad}</div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={onZamknij} style={btnSecondary} data-testid="osoz-zamknij">{wynik ? 'Zamknij' : 'Anuluj'}</button>
          {!wynik && (
            <button type="button" onClick={zapisz} disabled={wysylanie} data-testid="osoz-zapisz"
              style={{ ...btnPrimary, opacity: wysylanie ? 0.6 : 1 }}>
              {wysylanie ? 'Dodaję…' : pokrycie ? 'Spróbuj ponownie' : 'Dodaj fakturę'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DodajOsozModal;
